<template>
  <v-app>
    <app-header v-if="!isPublicRoute" />

    <v-main>
      <v-container fluid class="pa-4 pa-md-6">
        <router-view v-slot="{ Component }">
          <transition name="page-fade" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </v-container>
    </v-main>

    <app-footer v-if="!isPublicRoute" />

    <!-- Global snackbar -->
    <v-snackbar
      v-model="appStore.snackbar.show"
      :timeout="appStore.snackbar.timeout"
      :color="appStore.snackbar.color"
      location="top"
    >
      {{ appStore.snackbar.text }}
      <template v-slot:actions>
        <v-btn variant="text" @click="appStore.hideSnackbar">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!-- TanStack Query Devtools (dev only) -->
    <VueQueryDevtools v-if="isDev" />
  </v-app>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAppStore } from '@/stores/app';
import { useSettingsQuery } from '@/composables/useSettings';
import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
import AppHeader from '@/components/layout/AppHeader.vue';
import AppFooter from '@/components/layout/AppFooter.vue';

const isDev = import.meta.env.DEV;

const appStore = useAppStore();
const route = useRoute();
const router = useRouter();

const isPublicRoute = computed((): boolean => route.meta.public === true);

// Settings are auto-fetched by TanStack Query
useSettingsQuery();

// When the session is lost (e.g. it expires mid-use and the auth interceptor
// clears the cached user), send the user back to the login page.
watch(
  () => appStore.currentUser,
  (user) => {
    if (!user && !isPublicRoute.value) {
      void router.replace({ name: 'login', query: { redirect: route.fullPath } });
    }
  },
);
</script>
